import {FC, useCallback, useEffect, useState} from 'react'
import {KTSVG} from '../../../../../../_metronic/helpers'
import WSButton from '../../../../../../_metronic/helpers/components/WSButton/WSButton'
import {Automation} from '../../../../../../services/cellcast.v2.types'
import {Alert, Form} from 'react-bootstrap'
import Select from 'react-select'
import {SelectOption} from '../../../../../types'

type InputAutomationProps = {
  data: Automation
  setData: (automation: Automation) => void
  tagOptions: SelectOption<string>[]
  error: string
  handleClose: () => void
  handleNext: () => void
}

const searchTerms = [
  'anyMessage',
  'messageStartsWith',
  'messageContains',
  'messageEquals',
  'anyMessageOptOut',
]
const searchTeamsVisible = searchTerms.filter(
  (el) => el !== 'anyMessage' && el !== 'anyMessageOptOut'
)

const ruleOptions: SelectOption<string>[] = [
  {
    label: 'select rule',
    value: '',
  },
  ...searchTerms.map((el) => ({
    label: el,
    value: el,
  })),
]

const ruleContactTagUpdateOptions: SelectOption<string>[] = [
  {
    label: 'any tag',
    value: 'anyTag',
  },
  {
    label: 'include tag',
    value: 'includeTag',
  },
  {
    label: 'exclude tag',
    value: 'excludeTag',
  },
]

const triggerOptions: SelectOption<string>[] = [
  {
    label: 'select trigger',
    value: '',
  },
  {
    label: 'contact tag update',
    value: 'contactTagUpdate',
  },
  {
    label: 'incoming message',
    value: 'replyMessage',
  },
]

const InputAutomation: FC<InputAutomationProps> = (props) => {
  const {data, error, tagOptions, setData, handleClose, handleNext} = props

  const [isActiveValidate, setIsActiveValidate] = useState(false)
  const [isDisable, setIsDisable] = useState(false)
  const [inputError, setInputError] = useState<Record<string, any>>({})

  const validate = useCallback(
    (payload) => {
      let isValid = true
      if (!payload.title || (payload.title && payload.title.length === 0)) {
        setInputError({...inputError, title: 'title is required'})
        if (isValid) isValid = false
      } else {
        if (inputError.hasOwnProperty('title')) {
          delete inputError['title']
        }
      }

      if (!payload.trigger || (payload.trigger && payload.trigger.length === 0)) {
        setInputError({...inputError, rule: 'trigger is required'})
        if (isValid) isValid = false
      } else {
        if (inputError.hasOwnProperty('trigger')) {
          delete inputError['trigger  ']
        }
      }

      if (!payload.rule || (payload.rule && payload.rule.length === 0)) {
        setInputError({...inputError, rule: 'rule is required'})
        if (isValid) isValid = false
      } else {
        if (inputError.hasOwnProperty('rule')) {
          delete inputError['rule']
        }
      }

      if (payload.rule && searchTeamsVisible.includes(payload.rule)) {
        if (!payload.searchTerm || (payload.searchTerm && payload.searchTerm.length === 0)) {
          setInputError({
            ...inputError,
            searchTerm: 'searchTerm is required',
          })
          if (isValid) isValid = false
        } else {
          if (inputError.hasOwnProperty('searchTerm')) {
            delete inputError['searchTerm']
          }
        }
      }

      if (!isValid) {
        setIsActiveValidate(true)
        setIsDisable(true)
      } else {
        setIsDisable(false)
      }
      return isValid
    },
    [inputError, setInputError]
  )

  const handleNextLocal = useCallback(() => {
    const isValid = validate(data)
    if (!isValid) {
      setIsDisable(true)
      return
    }
    return handleNext()
  }, [data, handleNext, validate])

  const handleCloseLocal = useCallback(() => {
    setIsActiveValidate(false)
    setIsDisable(false)
    handleClose()
  }, [handleClose])

  useEffect(() => {
    if (isActiveValidate === true) {
      validate(data)
    }
  }, [data, isActiveValidate])

  return (
    <>
      <div className='modal-header'>
        <h2 className='fs-2'>{data._id ? 'Update' : 'Create'} Multi Step Automation</h2>
        <div className='btn btn-icon btn-sm' onClick={() => handleCloseLocal()}>
          <KTSVG className='svg-icon-1' path='/media/icons/duotune/arrows/arr061.svg' />
        </div>
      </div>
      <div className='modal-body py-lg-5 px-lg-5 border-bottom'>
        {error.length > 0 && (
          <Alert variant='danger'>
            <Alert.Heading>Error</Alert.Heading>
            <p>{error}</p>
          </Alert>
        )}
        <div className='mb-10 container'>
          <div className='row'>
            <label className='form-label required'>Workflow Name</label>
            <input
              onChange={(e) => {
                setData({...data, title: e.currentTarget.value})
              }}
              type='text'
              className='form-control form-control-solid bg-light border-0'
              placeholder='Worklfow Name'
              value={data.title}
            />
            {inputError.title && <span className='text-danger mt-2'>{inputError.title}</span>}
          </div>

          <div className='row mt-5'>
            <label className='form-label'>Status</label>
            <div className='d-flex align-items-center gap-4'>
              <Form.Check
                onChange={(e) => {
                  setData({...data, status: e?.target?.checked})
                }}
                checked={data.status}
                className='unsub-switch'
                type='switch'
                id='custom-switch'
              />
              <h6
                style={{
                  fontWeight: 500,
                  margin: 0,
                }}
              >
                {data.status ? 'Active' : 'Not Active'}
              </h6>
            </div>
            {inputError.status && <span className='text-danger mt-2'>{inputError.status}</span>}
          </div>

          <div className='row mt-5'>
            <label className='form-label required'>Trigger</label>
            <Select
              styles={{
                container: (provided) => ({
                  ...provided,
                  paddingLeft: 0,
                  paddingRight: 0,
                }),
              }}
              onChange={(e: any) => {
                const trigger = e.value as any
                setData({...data, trigger: trigger})
              }}
              value={triggerOptions.find((el) => el.value === data.trigger) || triggerOptions[0]}
              options={triggerOptions as any}
            />
            {inputError.trigger && <span className='text-danger mt-2'>{inputError.trigger}</span>}
          </div>

          {data.trigger !== '' && data.trigger === 'replyMessage' && (
            <>
              <div className='row mt-5'>
                <label className='form-label'>Rule</label>
                <Select
                  styles={{
                    container: (provided) => ({
                      ...provided,
                      paddingLeft: 0,
                      paddingRight: 0,
                    }),
                  }}
                  onChange={(e: any) => {
                    const rule = e.value as any
                    setData({...data, rule: rule, searchTerm: ''})
                  }}
                  value={ruleOptions.find((el) => el.value === data.rule) || ruleOptions[0]}
                  options={ruleOptions as any}
                />
                {inputError.rule && <span className='text-danger mt-2'>{inputError.rule}</span>}
              </div>
              {searchTeamsVisible.includes(data?.rule) ? (
                <div className='row mt-5'>
                  <div className='mt-0'>
                    <label className='form-label'>Search term</label>
                    <input
                      type='text'
                      className='form-control form-control-lg form-control-solid'
                      placeholder='Input terms'
                      value={data?.searchTerm}
                      onChange={(e) => {
                        setData({...data, searchTerm: e.target.value})
                      }}
                    />
                    {inputError.searchTerm && (
                      <span className='text-danger mt-2'>{inputError.searchTerm}</span>
                    )}
                  </div>
                </div>
              ) : null}
            </>
          )}

          {data.trigger !== '' && data.trigger === 'contactTagUpdate' && (
            <>
              <div className='row mt-5'>
                <label className='form-label'>Rule</label>
                <Select
                  styles={{
                    container: (provided) => ({
                      ...provided,
                      paddingLeft: 0,
                      paddingRight: 0,
                    }),
                  }}
                  onChange={(e: any) => {
                    const rule = e.value as any
                    setData({...data, rule: rule, searchTerm: ''})
                  }}
                  value={
                    ruleContactTagUpdateOptions.find((el) => el.value === data.rule) ||
                    ruleContactTagUpdateOptions[0]
                  }
                  options={ruleContactTagUpdateOptions as any}
                />
                {inputError.rule && <span className='text-danger mt-2'>{inputError.rule}</span>}
              </div>
              {data.rule === 'includeTag' || data.rule === 'excludeTag' ? (
                <div className='row mt-5'>
                  <label className='form-label'>Tags</label>
                  <Select
                    styles={{
                      container: (provided) => ({
                        ...provided,
                        paddingLeft: 0,
                        paddingRight: 0,
                      }),
                    }}
                    onChange={(e: any) => {
                      let tagIds = []
                      if (Array.isArray(e)) {
                        tagIds = e.flatMap((e) => e.value)
                      }
                      setData({...data, searchTerm: tagIds})
                    }}
                    value={tagOptions.filter((o) => {
                      if (Array.isArray(data.searchTerm)) {
                        return data.searchTerm.includes(o.value)
                      }
                      return false
                    })}
                    isMulti={true}
                    options={tagOptions as any}
                  />
                  {inputError.rule && <span className='text-danger mt-2'>{inputError.rule}</span>}
                </div>
              ) : (
                <></>
              )}
            </>
          )}
        </div>

        <div className='container d-flex align-items-end justify-content-end mt-6 mb-5'>
          <WSButton
            type='submit'
            className='btn btn-lg btn-light-primary me-5 fw-500'
            onClick={() => handleCloseLocal()}
          >
            <span className='indicator-label'>Cancel</span>
          </WSButton>
          <WSButton
            type='submit'
            disabled={isDisable}
            className='btn btn-lg btn-primary fw-500'
            onClick={() => handleNextLocal()}
          >
            <span className='indicator-label'>Next</span>
          </WSButton>
        </div>
      </div>
    </>
  )
}

export default InputAutomation
